/** The draft as text (E3, spec 2026-09-18 §6 Edit): YAML by default with a
 * JSON toggle. It round-trips because the raw document IS the draft — there
 * is no second model. `onChange` fires only on a clean parse to a mapping; a
 * syntax error shows inline and leaves the document untouched.
 *
 * The text is the author's while they type (their spacing survives); it is
 * re-rendered from `doc` only when the document changes from OUTSIDE — a form
 * edit, a reload. The draft is stored as JSON, so YAML comments do not
 * survive a save. `onInvalidChange`, optional, hears whether the text on
 * screen fails to parse, so a caller can count it as unsaved (SA-1387). */
import { useEffect, useRef, useState } from 'react'
import { parse, stringify } from 'yaml'
import { Button, Textarea } from '@/components/ui'

type Format = 'yaml' | 'json'

const render = (doc: unknown, format: Format) =>
  format === 'yaml' ? stringify(doc, { lineWidth: 0 }) : JSON.stringify(doc, null, 2)

export function RawDefinitionEditor({
  doc,
  onChange,
  onInvalidChange,
}: {
  doc: Record<string, unknown>
  onChange: (doc: Record<string, unknown>) => void
  /** Called with whether the text fails to parse, whenever that changes (and once on mount). */
  onInvalidChange?: (invalid: boolean) => void
}) {
  const [format, setFormat] = useState<Format>('yaml')
  const [text, setText] = useState(() => render(doc, 'yaml'))
  const [error, setError] = useState<string | null>(null)
  // What the text currently parses to — lets an incoming `doc` that is just
  // our own edit coming back be told apart from an outside change.
  const parsedJson = useRef(JSON.stringify(doc))
  const invalid = error !== null
  useEffect(() => { onInvalidChange?.(invalid) }, [invalid, onInvalidChange])

  useEffect(() => {
    const incoming = JSON.stringify(doc)
    if (incoming === parsedJson.current) return
    parsedJson.current = incoming
    setText(render(doc, format))
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc])

  function edit(next: string) {
    setText(next)
    let parsed: unknown
    try {
      parsed = format === 'yaml' ? parse(next) : JSON.parse(next)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('The document must be a mapping of keys, not a list or a single value.')
      return
    }
    setError(null)
    parsedJson.current = JSON.stringify(parsed)
    onChange(parsed as Record<string, unknown>)
  }

  function switchFormat(next: Format) {
    if (next === format) return
    // Re-render from the last CLEAN document: a half-typed text has no
    // other format to be shown in.
    setFormat(next)
    setText(render(JSON.parse(parsedJson.current), next))
    setError(null)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          The whole definition. Edits here and in the form are the same draft.
        </p>
        <div role="group" aria-label="Format" className="flex items-center gap-1">
          {(['yaml', 'json'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={format === f ? 'secondary' : 'ghost'}
              aria-pressed={format === f}
              // The other format can only show a CLEAN document: switching
              // now would throw the half-typed text away.
              disabled={error !== null && format !== f}
              title={error !== null && format !== f ? 'Fix the text first — switching would discard it' : undefined}
              onClick={() => switchFormat(f)}
            >
              {f.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>
      <Textarea
        aria-label="Definition source"
        aria-invalid={error !== null}
        spellCheck={false}
        rows={28}
        className="font-mono text-xs"
        value={text}
        onChange={(e) => edit(e.target.value)}
      />
      {error && (
        <p role="alert" className="text-xs text-destructive">
          Could not parse — the draft keeps its last valid content. {error}
        </p>
      )}
    </div>
  )
}
